import { FeedItem } from "../types/feed";
import { FacetToken } from "../types/facets";
import { formatRelativeTime } from "../utils/formatters";
import { ReactNode } from "react";
import {
  IconCheckbox,
  IconClipboardCheck,
  IconBoomFilled,
  IconBombFilled,
  IconCircleDashed,
  IconGasStation,
  IconGrave2,
  IconParachute,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconPlugConnected,
  IconPlugConnectedX,
  IconRefresh,
  IconTarget,
} from "@tabler/icons-react";

interface FeedTableProps {
  items: FeedItem[];
  onAddFacet?: (token: FacetToken) => void;
  onRemoveFacet?: (token: FacetToken) => void;
  activeTokens?: FacetToken[];
  showPills?: boolean;
  groupByDate?: boolean;
}

const FeedTable = ({
  items,
  onAddFacet,
  onRemoveFacet,
  activeTokens = [],
  showPills = true,
  groupByDate = false
}: FeedTableProps) => {
  if (!items.length) {
    return <div className="empty-state">No feed items found. Adjust filters to broaden the search.</div>;
  }

  const groupedItems = groupByDate ? buildDateGroups(items) : [ { label: "", items } ];

  return (
    <div className="feed-list" role="list">
      {groupedItems.map((group) => (
        <div key={group.label || "all"} className="feed-group">
          {group.label && <div className="feed-group-header">{group.label}</div>}
          {group.items.map((item) => (
            <article key={item.id} className={"feed-card" + (!showPills ? " feed-card-compact" : "")} role="listitem">
              <div className={"feed-icon " + iconClassForItem(item)} aria-hidden="true">
                {renderIcon(item)}
              </div>

              <div className="feed-main">
                {showPills ? (
                  <>
                    <div className="feed-headline">{renderHeadlineForItem(item)}</div>

                    <div className="feed-meta">
                      {renderPills(item, {
                        onAddFacet,
                        onRemoveFacet,
                        activeTokens
                      })}
                      <span className="feed-time">{formatRelativeTime(item.occurred_at)}</span>
                    </div>
                  </>
                ) : (
                  <div className="feed-inline-row">
                    <div className="feed-headline">{renderHeadlineForItem(item)}</div>
                    <span className="feed-time">{formatRelativeTime(item.occurred_at)}</span>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      ))}
    </div>
  );
};

function renderHeadlineForItem(item: FeedItem): ReactNode {
  const template = typeof item.summary_template === "string" ? item.summary_template : "";
  const vars = item.summary_vars && typeof item.summary_vars === "object" ? item.summary_vars : {};

  const rendered = renderMustacheTemplate(template, vars);
  const summary = typeof item.summary === "string" ? item.summary : "";
  const text = (rendered || summary || item.title).trim() || item.title;

  const uidByActorName = new Map<string, string>();
  for (const actor of item.actors) {
    const name = typeof actor.name === "string" ? actor.name.trim() : "";
    const uid = typeof actor.uid === "string" ? actor.uid.trim() : "";
    if (!name || !uid) continue;
    if (!uidByActorName.has(name)) uidByActorName.set(name, uid);
  }

  if (uidByActorName.size === 0) return text;

  const links = Array.from(uidByActorName.entries()).map(([name, uid]) => ({
    name,
    href: profileHref(uid),
  }));

  return linkifyNames(text, links);
}

function linkifyNames(text: string, links: Array<{ name: string; href: string }>): ReactNode {
  const normalizedLinks = links
    .map((l) => ({ name: l.name.trim(), href: l.href }))
    .filter((l) => l.name.length > 0);

  if (!text || normalizedLinks.length === 0) return text;

  // Prefer longer names first to reduce partial matches (e.g., "Bob" inside "Bobby").
  normalizedLinks.sort((a, b) => b.name.length - a.name.length);

  const hrefByName = new Map(normalizedLinks.map((l) => [l.name, l.href] as const));
  const pattern = normalizedLinks.map((l) => escapeRegExp(l.name)).join("|");
  if (!pattern) return text;

  const regex = new RegExp(pattern, "g");
  const nodes: ReactNode[] = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const name = match[0];
    const start = match.index;
    const end = start + name.length;

    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));

    const href = hrefByName.get(name);
    if (href) {
      nodes.push(
        <a
          key={`${name}:${start}`}
          href={href}
          className="feed-headline-link"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          title={`Open profile for ${name}`}
          aria-label={`Open profile for ${name}`}
        >
          {name}
        </a>
      );
    } else {
      nodes.push(name);
    }

    lastIndex = end;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length ? nodes : text;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderMustacheTemplate(
  template: string,
  vars: Record<string, string | number | boolean | null>
): string {
  if (!template) return "";

  return template
    .replace(/\{\{\s*([^}\s]+)\s*\}\}/g, (_match, key: string) => {
      const value = vars[key];
      return value == null ? "" : String(value);
    })
    .replace(/\s+/g, " ")
    .trim();
}

function renderPills(
  item: FeedItem,
  actions: {
    onAddFacet?: (token: FacetToken) => void;
    onRemoveFacet?: (token: FacetToken) => void;
    activeTokens?: FacetToken[];
  }
) {
  const pills: Array<{
    key: string;
    label: string;
    tone?: string;
    token?: FacetToken;
    href?: string;
    statsHref?: string;
  }> = [];

  if (item.kind !== "event") {
    pills.push({
      key: `kind:${item.kind}`,
      label: item.kind,
      tone: "unit",
      token: { key: "kind", value: item.kind }
    });
  }

  if (item.event_type) {
    pills.push({
      key: `event:${item.event_type}`,
      label: item.event_type.replace(/_/g, "-"),
      token: { key: "event", value: item.event_type }
    });
  }

  const actorNames: string[] = [];
  const seenActorNames = new Set<string>();
  const unknownActors: string[] = [];
  const uidByActorName = new Map<string, string>();

  for (const actor of item.actors) {
    const name = typeof actor.name === "string" ? actor.name.trim() : "";
    if (name) {
      if (seenActorNames.has(name)) continue;
      seenActorNames.add(name);
      actorNames.push(name);

      if (actor.uid && !uidByActorName.has(name)) {
        uidByActorName.set(name, actor.uid);
      }
    } else if (actor.uid) {
      unknownActors.push(actor.uid);
    }
  }

  for (const name of actorNames) {
    const uid = uidByActorName.get(name);
    pills.push({
      key: `pilot:${name}`,
      label: name,
      tone: "pilot",
      token: { key: "pilot", value: name },
      statsHref: uid ? profileHref(uid) : undefined,
    });
  }

  // If the API provides a UID without a name, show it as non-clickable context
  // (we avoid offering a filter token that likely won't match by name).
  for (const uid of unknownActors) {
    pills.push({
      key: `actor_uid:${uid}`,
      label: "Unknown pilot",
      tone: "pilot",
      href: profileHref(uid)
    });
  }

  const unit = item.metadata.unit_type;
  if (unit) {
    pills.push({ key: `unit:${unit}`, label: unit, tone: "unit", token: { key: "unit", value: unit } });
  }

  const weapon = item.metadata.weapon_name;
  if (weapon) {
    pills.push({ key: `weapon:${weapon}`, label: weapon, tone: "weapon", token: { key: "weapon", value: weapon } });
  }

  const serverLabel = item.server_name || (item.server_id ? `Server ${item.server_id}` : null);
  if (serverLabel) {
    pills.push({
      key: `server:${serverLabel}`,
      label: serverLabel,
      tone: "server",
      token: { key: "server", value: serverLabel }
    });
  }

  const airdrome = item.metadata.airdrome_name;
  if (airdrome && !weapon) {
    pills.push({
      key: `airdrome:${airdrome}`,
      label: airdrome,
      tone: "place",
      token: { key: "airdrome", value: airdrome }
    });
  }

  return pills.map((pill) => {
    const isActive = pill.token && actions.activeTokens?.some(
      (t) => t.key === pill.token!.key && t.value === pill.token!.value
    );
    const isClickable = pill.token && (actions.onAddFacet || actions.onRemoveFacet);
    const className = "pill" +
      (pill.tone ? ` pill-${pill.tone}` : "") +
      (isClickable || pill.href ? " pill-clickable" : "") +
      (isActive ? " pill-active" : "");

    if (pill.token && (actions.onAddFacet || actions.onRemoveFacet)) {
      const label = `${pill.token.key}:${pill.token.value}`;
      const action = isActive ? actions.onRemoveFacet : actions.onAddFacet;
      const actionLabel = isActive ? "Remove" : "Add";

      return (
        <button
          key={pill.key}
          type="button"
          className={className}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            if (pill.statsHref && (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey)) {
              window.location.hash = pill.statsHref.replace(/^#/, "");
              return;
            }
            action?.(pill.token!);
          }}
          title={
            pill.statsHref
              ? `${actionLabel} ${label} ${isActive ? "from" : "to"} filters (modifier-click for profile)`
              : `${actionLabel} ${label} ${isActive ? "from" : "to"} filters`
          }
          aria-label={
            pill.statsHref
              ? `${actionLabel} ${label} ${isActive ? "from" : "to"} filters. Modifier-click opens the profile.`
              : `${actionLabel} ${label} ${isActive ? "from" : "to"} filters`
          }
          aria-pressed={isActive}
        >
          {pill.label}
        </button>
      );
    }

    if (pill.href) {
      return (
        <a
          key={pill.key}
          className={className}
          href={pill.href}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          title={`Open profile for ${pill.label}`}
          aria-label={`Open profile for ${pill.label}`}
        >
          {pill.label}
        </a>
      );
    }

    return (
      <span key={pill.key} className={className}>
        {pill.label}
      </span>
    );
  });
}

function profileHref(uid: string): string {
  return `#/pilots/${encodeURIComponent(uid)}`;
}

function buildDateGroups(items: FeedItem[]): Array<{ label: string; items: FeedItem[] }> {
  const groups = new Map<string, FeedItem[]>();

  items.forEach((item) => {
    const label = dateGroupLabel(item.occurred_at);
    const rows = groups.get(label) ?? [];
    rows.push(item);
    groups.set(label, rows);
  });

  return Array.from(groups.entries()).map(([label, grouped]) => ({ label, items: grouped }));
}

function dateGroupLabel(value: string): string {
  const date = new Date(value);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.floor((today - day) / 86400000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function iconClassForItem(item: FeedItem): string {
  const type = resolveIconType(item);

  if (type === "kill" || type === "self_kill" || type === "pilot_death" || type === "crash") {
    return "icon-danger";
  }

  if (type === "eject") {
    return "icon-warning";
  }

  if (type === "landing") {
    return "icon-success";
  }

  if (type === "proficiency" || type === "achievement") {
    return "icon-success";
  }

  if (type === "connect" || type === "disconnect" || type === "change_slot" || type === "takeoff") {
    return "icon-info";
  }

  if (type === "aar") {
    return "icon-info";
  }

  if (type === "grading") {
    return "icon-info";
  }

  return "icon-neutral";
}

function renderIcon(item: FeedItem) {
  const type = resolveIconType(item);

  const commonProps = {
    size: ICON_SIZE,
    stroke: ICON_STROKE,
  };

  switch (type) {
    case "proficiency":
      return <IconCheckbox {...commonProps} />;
    case "achievement":
      return <IconCheckbox {...commonProps} />;
    case "connect":
      return <IconPlugConnected {...commonProps} />;
    case "disconnect":
      return <IconPlugConnectedX {...commonProps} />;
    case "change_slot":
      return <IconRefresh {...commonProps} />;
    case "takeoff":
      return <IconPlaneDeparture {...commonProps} />;
    case "aar":
      return <IconGasStation {...commonProps} />;
    case "landing":
      return <IconPlaneArrival {...commonProps} />;
    case "kill":
      return <IconTarget {...commonProps} />;
    case "grading":
      return <IconClipboardCheck {...commonProps} />;
    case "eject":
      return <IconParachute {...commonProps} />;
    case "crash":
      return <IconBoomFilled {...commonProps} />;
    case "pilot_death":
      return <IconGrave2 {...commonProps} />;
    case "self_kill":
      return <IconBombFilled size={ICON_SIZE} />;
    default:
      return <IconCircleDashed {...commonProps} />;
  }
}

type FeedIconType =
  | "proficiency"
  | "achievement"
  | "connect"
  | "disconnect"
  | "change_slot"
  | "takeoff"
  | "aar"
  | "landing"
  | "grading"
  | "kill"
  | "eject"
  | "crash"
  | "pilot_death"
  | "self_kill";

const ICON_SIZE = 20;
const ICON_STROKE = 2.0;

function resolveIconType(item: FeedItem): FeedIconType | "default" {
  if (item.kind === "proficiency") return "proficiency";
  if (item.kind === "achievement") return "achievement";

  const raw = item.event_type?.toLowerCase() ?? "";
  if (!raw) return "default";

  if (raw.includes("disconnect")) return "disconnect";
  if (raw.includes("change") && raw.includes("slot")) return "change_slot";
  if (raw.includes("connect")) return "connect";
  if (raw.includes("takeoff") || raw.includes("take_off") || raw.includes("take off")) return "takeoff";
  if (raw.includes("aar") || raw.includes("refuel")) return "aar";
  if (raw.includes("grading") || raw.includes("grade")) return "grading";
  if (raw.includes("pilot") && raw.includes("death")) return "pilot_death";
  if (raw.includes("self") && raw.includes("kill")) return "self_kill";
  if (raw.includes("crash")) return "crash";
  if (raw.includes("kill")) return "kill";
  if (raw.includes("eject")) return "eject";
  if (raw.includes("landing") || raw.includes("land")) return "landing";

  return "default";
}

export default FeedTable;
